import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '@/stores/auth.store';
import { useUpdateUser } from '@/hooks/users/use-update-user';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';

const ProfileFormSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required').max(100),
  lastName: z.string().trim().min(1, 'Last name is required').max(100),
});

type ProfileFormInput = z.infer<typeof ProfileFormSchema>;

export function ProfileSettings() {
  const user = useAuthStore((state) => state.user);
  const updateUserMutation = useUpdateUser();
  const { toast } = useToast();

  const form = useForm<ProfileFormInput>({
    resolver: zodResolver(ProfileFormSchema),
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
    },
  });

  const onSubmit = (data: ProfileFormInput) => {
    if (!user) return;
    updateUserMutation.mutate(
      {
        id: user.id,
        input: data,
      },
      {
        onSuccess: () => {
          toast({
            title: 'Profile Updated',
            description: 'Your profile details have been saved successfully.',
          });
        },
        onError: (err: any) => {
          toast({
            variant: 'destructive',
            title: 'Update Failed',
            description: err.response?.data?.error?.message || 'Failed to update profile.',
          });
        },
      }
    );
  };

  return (
    <Card className="border border-border/50 bg-bg-elevated/80 shadow-md">
      <CardHeader>
        <CardTitle className="text-xl text-fg-primary">Personal Profile</CardTitle>
        <CardDescription className="text-fg-secondary">
          Manage your account profile details and display name.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <Label className="text-fg-secondary">Email Address</Label>
            <Input type="email" value={user?.email || ''} disabled className="bg-bg-subtle/50" />
            <p className="text-[11px] text-fg-muted">Your email address cannot be changed.</p>
          </div>
          <div className="space-y-1">
            <Label className="text-fg-secondary">Assigned Role</Label>
            <Input type="text" value={user?.roleCode || ''} disabled className="bg-bg-subtle/50" />
            <p className="text-[11px] text-fg-muted">Your role can only be modified by administrators.</p>
          </div>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pt-4 border-t border-border/50">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <Input placeholder="John" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Doe" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end pt-2">
              <Button
                type="submit"
                className="bg-primary hover:bg-primary/95 text-primary-foreground font-semibold px-6"
                disabled={updateUserMutation.isPending}
              >
                {updateUserMutation.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
